// Wave AW.3-React (2026-05-16) — PROMISE (RFQ) 목록 화면
// API: GET /buyer-app/rfq — open RFQ 목록 (매너 ≥ 30°)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import EmptyState from './EmptyState';
import { useT } from '../../i18n/I18nProvider';
import { formatKrwAmount } from '../../i18n/currency';

// 카드 좁은 공간 — base only (환산은 상세에서)
function formatKrw(n, t) { return formatKrwAmount(n, t); }
function formatMileage(km) { if (km == null) return ''; return Number(km).toLocaleString() + ' km'; }

// RFQ 사진 URL — photos 는 array<string> 또는 object<slot, string>
function getThumbUrl(rfq) {
  var photos = rfq.photos;
  if (!photos) return null;
  var path = null;
  if (Array.isArray(photos)) {
    path = photos[0];
  } else if (typeof photos === 'object') {
    // legacy named slots: exterior / interior / ... 우선
    path = photos.exterior || photos.front || photos[Object.keys(photos)[0]];
  }
  if (!path) return null;
  // path 가 'uploads/...' 또는 '/uploads/...' 또는 absolute URL
  if (/^https?:/.test(path)) return path;
  var origin = API.replace(/\/api$/, '');
  return origin + (path.charAt(0) === '/' ? path : '/' + path);
}

function PromiseCard(props) {
  var t = useT();
  var rfq = props.rfq;
  var thumb = getThumbUrl(rfq);
  var hasMyQuote = !!rfq.ownQuote;
  var myPrice = hasMyQuote ? rfq.ownQuote.priceKrw : 0;

  return (
    <div onClick={props.onClick}
      className="buyer-card-lift"
      style={{
        background: 'white', borderRadius: 12,
        border: '1px solid #e2e8f0',
        cursor: 'pointer', overflow: 'hidden',
      }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '4/3', background: '#f1f5f9' }}>
        {thumb ? (
          <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>
            {t('cardNoPhoto')}
          </div>
        )}
        {hasMyQuote && (
          <div style={{ position: 'absolute', top: 10, left: 10, background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
            {t('promiseCardMyQuote', { amount: formatKrw(myPrice, t) })}
          </div>
        )}
        {rfq.ownerConsented && (
          <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(5,150,105,0.92)', color: 'white', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>
            {t('promiseCardOwnerConsented')}
          </div>
        )}
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {rfq.year ? rfq.year + ' ' : ''}{rfq.model || t('promiseCardCarFallback')}
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', gap: 8, marginBottom: 8 }}>
          {rfq.carNo && <span>{rfq.carNo}</span>}
          {formatMileage(rfq.mileage) && <span>·</span>}
          {formatMileage(rfq.mileage) && <span>{formatMileage(rfq.mileage)}</span>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #f1f5f9', fontSize: 11, color: '#94a3b8' }}>
          <span>{t('promiseCardQuoteCount', { count: rfq.quoteCount || 0 })}</span>
          {rfq.sellerTier && rfq.sellerTier.label && (
            <span style={{ color: rfq.sellerTier.color || '#94a3b8', fontWeight: 700 }}>{rfq.sellerTier.short || rfq.sellerTier.label}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function AuctionBuyerPromiseList() {
  var navigate = useNavigate();
  var t = useT();
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');

  var load = useCallback(function() {
    setLoading(true); setError('');
    authBuyerFetch(API + '/buyer-app/rfq?limit=50&offset=0', {})
      .then(function(r){
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d){
        if (d && d.success && Array.isArray(d.data)) {
          setItems(d.data);
        } else if (d && d.code === 'BUYER_TRUST_TOO_LOW') {
          setError(t('errMinTrust'));
        } else {
          setError(displayApiError(d, t, 'errListLoad'));
        }
        setLoading(false);
      })
      .catch(function(e){
        if (e && e.message === 'unauthorized') return;
        setError(t('commonNetwork')); setLoading(false);
      });
  }, [navigate, t]);

  useEffect(function(){ load(); }, [load]);

  return (
    <AuctionBuyerLayout activeKey="promise">
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 32px 80px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{t('promiseTitle')}</h2>
        </div>
        <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
          {t('promiseSubtitle')}
        </p>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 14, marginBottom: 20, color: '#991b1b', fontSize: 14 }}>
            ⚠ {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>{t('auctionsListLoading')}</div>
        ) : items.length === 0 ? (
          <EmptyState icon="📋" title={t('promiseListEmpty')} hint={t('promiseListEmptyHint')} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {items.map(function(rfq){
              return (
                <PromiseCard key={rfq.id} rfq={rfq}
                  onClick={function(){ navigate('/buyer/promise/' + rfq.id); }}
                />
              );
            })}
          </div>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerPromiseList;
